// Aligned-crop -> worker-bound input pixels (pipeline Stage 4 preprocessing).
//
// The model's IO contract mandates the SAME alignment used at training time:
// the src/lib/facePrep normalized 1080x1440 frame, downscaled to 224 (the
// Colab reference used INTER_AREA). We downscale through a 2D canvas with
// high-quality smoothing (area-averaging) and read pixels straight from the
// canvas — BEFORE any JPEG encode — so we don't inherit recompression drift
// that would shift the raw score away from the calibration reference.
//
// The downscaled RGBA bytes are handed to the inference Web Worker; the worker
// owns the tf.browser.fromPixels → /255 tensor build (off the main thread).
import { INPUT_SIZE } from './constants';

// Downscale any canvas/ImageBitmap-like source to an INPUT_SIZE square canvas.
// Exported for unit testing independent of the worker.
export function downscaleToInput(source) {
  if (typeof document === 'undefined' || !document.createElement) {
    throw new Error('No DOM canvas available.');
  }
  const out = document.createElement('canvas');
  out.width = INPUT_SIZE;
  out.height = INPUT_SIZE;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('No 2D canvas.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high'; // ~INTER_AREA for downscale
  ctx.drawImage(source, 0, 0, INPUT_SIZE, INPUT_SIZE);
  return out;
}

// Aligned canvas -> { width, height, buffer } of INPUT_SIZE RGBA pixels. The
// buffer is the ImageData's underlying ArrayBuffer, posted to the worker as a
// transferable (zero-copy) — so the caller must not reuse it afterward.
export function canvasToInputImageData(source) {
  const small = downscaleToInput(source);
  const ctx = small.getContext('2d');
  if (!ctx || !ctx.getImageData) throw new Error('No 2D canvas.');
  const img = ctx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);
  return { width: img.width, height: img.height, buffer: img.data.buffer };
}
